import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronDown, ChevronRight, Ellipsis, Link2, LogOut, Pencil, Trash2, UserPlus } from "lucide-react";

import { AppStateContext } from "@/state/AppStateContext";
import { Conversation } from "@/api/models";
import { deleteGroupConversation, leaveGroupConversation, renameGroupConversation } from "@/api/api";
import InviteParticipantsModal from "@/components/GroupConversation/InviteParticipantsModal";
import { mergeGroupParticipants, replaceConversationParticipants, syncGroupConversationState } from "@/components/GroupConversation/groupConversationState";
import { clearAgent, clearChatTransientState, clearCustomGPT } from "@/utils/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const containerVariants = {
  collapsed: { height: 0, opacity: 0, transition: { when: "afterChildren" } },
  expanded: {
    height: "auto",
    opacity: 1,
    transition: { staggerChildren: 0.05, when: "beforeChildren" },
  },
};

const itemVariants = {
  collapsed: { opacity: 0, y: -10 },
  expanded: { opacity: 1, y: 0 },
};

const GroupChatHistoryList = () => {
  const appStateContext = useContext(AppStateContext);
  const [expanded, setExpanded] = useState(true);
  const [groupChats, setGroupChats] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (localStorage.getItem("sms-gpt_groups-expanded") === "0") setExpanded(false);
  }, []);

  useEffect(() => {
    if (appStateContext?.state.groupChats !== null) {
      const sorted = [...(appStateContext?.state.groupChats || [])].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      setGroupChats(sorted);
      setLoading(false);
    }
  }, [appStateContext?.state.groupChats]);

  const toggleExpanded = () => {
    const newValue = !expanded;
    setExpanded(newValue);
    localStorage.setItem("sms-gpt_groups-expanded", newValue ? "1" : "0");
  };

  if (loading) return null;

  return (
    <div className="text-[13px] px-1 mt-1">
      <button
        type="button"
        className="btn btn-xs btn-ghost justify-start items-center w-full text-black/50 dark:text-white/50 text-xs p-0 mt-4 tour-step-8"
        onClick={toggleExpanded}
      >
        Group Chats
        {expanded ? <ChevronDown size={14} className="mt-0.5" /> : <ChevronRight size={14} className="mt-0.5" />}
      </button>

      <motion.div
        initial="collapsed"
        animate={expanded ? "expanded" : "collapsed"}
        variants={containerVariants}
        style={{ overflow: "hidden" }}
      >
        <AnimatePresence>
          {groupChats.map((chat, idx) => (
            <motion.div
              key={chat.id ?? idx}
              variants={itemVariants}
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
            >
              <GroupChatItem item={chat} />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

const GroupChatItem = ({ item }: { item: Conversation }) => {
  const appStateContext = useContext(AppStateContext);
  const navigate = useNavigate();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameTitle, setRenameTitle] = useState(item.title || "");
  const [linkCopied, setLinkCopied] = useState(false);

  const customGPTName = appStateContext?.state.customGPTs?.find(c => c.id === item.customGPTID)?.name || "";

  const isSelected = item.id === appStateContext?.state.currentChat?.id;
  const currentUserId = appStateContext?.state.currentUser?.id;
  const isOwner = item.ownerId === currentUserId;

  const handleInvited = (invitedParticipants: Conversation["participants"] = []) => {
    const nextConversation = replaceConversationParticipants(
      item,
      mergeGroupParticipants(item.participants || [], invitedParticipants || []),
    );
    syncGroupConversationState(appStateContext?.dispatch, appStateContext?.state.currentChat, nextConversation);
  };

  const handleOpen = () => {
    clearChatTransientState(appStateContext);
    clearCustomGPT(appStateContext);
    clearAgent(appStateContext);
    appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: item });
    navigate(`/gc/${item.id}`);
    setIsPopoverOpen(false);
  };

  // Fully reset the open chat view (transient state, custom GPT, agent, panel
  // position) and navigate home — keeps behaviour in sync with GroupParticipants.
  const resetOpenChat = () => {
    clearChatTransientState(appStateContext);
    clearCustomGPT(appStateContext);
    clearAgent(appStateContext);
    appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: null });
    appStateContext?.dispatch({ type: "SET_CHATPANEL_TRANSITIONED", payload: false });
    navigate("/");
  };

  const handleLeave = () => {
    setIsLeaveDialogOpen(false);
    appStateContext?.dispatch({ type: "DELETE_GROUP_CHAT", payload: item.id });
    if (isSelected) resetOpenChat();
    leaveGroupConversation(item.id);
  };

  const handleDelete = () => {
    setIsDeleteDialogOpen(false);
    appStateContext?.dispatch({ type: "DELETE_GROUP_CHAT", payload: item.id });
    if (isSelected) resetOpenChat();
    deleteGroupConversation(item.id);
  };

  const handleRename = async () => {
    if (!renameTitle.trim() || renameTitle === item.title) {
      setIsRenaming(false);
      return;
    }
    const success = await renameGroupConversation(item.id, renameTitle.trim());
    if (!success) {
      setRenameTitle(item.title || "");
      setIsRenaming(false);
      return;
    }
    appStateContext?.dispatch({
      type: "UPDATE_GROUP_CHAT",
      payload: { ...item, title: renameTitle.trim() },
    });
    setIsRenaming(false);
    setIsPopoverOpen(false);
  };

  return (
    <div
      className={`group flex items-center gap-2 rounded-xl px-2 py-1.5 my-0.5 cursor-pointer select-none text-[13px]
        ${isSelected ? "bg-neutral" : "hover:bg-neutral"}`}
    >
      {isRenaming ? (
        <input
          autoFocus
          className="flex-1 bg-transparent outline-none text-sm border-b border-accent"
          value={renameTitle}
          onChange={e => setRenameTitle(e.target.value)}
          onBlur={handleRename}
          onKeyDown={e => {
            if (e.key === "Enter") handleRename();
            if (e.key === "Escape") { setIsRenaming(false); setRenameTitle(item.title || ""); }
          }}
        />
      ) : (
        <div className="flex-1 min-w-0" onClick={handleOpen}>
          <div className="truncate dark:text-white">{item.title || "Group Chat"}</div>
          {customGPTName && <div className="truncate text-[11px] ms-0.5 text-base-content/50">{customGPTName}</div>}
        </div>
      )}

      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={`[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 hover:bg-base-100 rounded-xl cursor-pointer px-1 shrink-0 ${isSelected ? "[@media(hover:hover)]:opacity-100" : ""}`}
            onClick={e => e.stopPropagation()}
          >
            <Ellipsis size={20} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="bg-secondary border-transparent rounded-3xl shadow-short w-[150px] dark:text-white p-3">
          {isOwner && (
            <button
              type="button"
              className="flex items-center gap-2 w-full rounded-xl hover:bg-neutral px-3 py-2 text-sm cursor-pointer"
              onClick={() => {
                navigator.clipboard.writeText(window.location.origin + "/gc/join/" + item.id);
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), 3000);
              }}
            >
              {linkCopied ? <><Check size={14} /> Copied</> : <><Link2 size={14} /> Copy Link</>}
            </button>
          )}
          {isOwner && (
            <InviteParticipantsModal
              conversationId={item.id}
              existingParticipants={item.participants || []}
              onInvited={handleInvited}
              trigger={
                <button
                  type="button"
                  className="flex items-center gap-2 w-full rounded-xl hover:bg-neutral px-3 py-2 text-sm cursor-pointer"
                >
                  <UserPlus size={14} /> Add People
                </button>
              }
            />
          )}
          <button
            type="button"
            className="flex items-center gap-2 w-full rounded-xl hover:bg-neutral px-3 py-2 text-sm cursor-pointer"
            onClick={() => { setIsRenaming(true); setIsPopoverOpen(false); }}
          >
            <Pencil size={14} /> Rename
          </button>
          {!isOwner && (
            <Dialog open={isLeaveDialogOpen} onOpenChange={setIsLeaveDialogOpen}>
              <DialogTrigger asChild>
                <button type="button" className="flex items-center gap-2 w-full rounded-xl hover:bg-neutral px-3 py-2 text-sm cursor-pointer">
                  <LogOut size={14} /> Leave
                </button>
              </DialogTrigger>
              <DialogContent className="bg-secondary border-transparent rounded-3xl shadow-short">
                <DialogHeader>
                  <DialogTitle className="mb-3">Leave group chat?</DialogTitle>
                  <DialogDescription>Are you sure that you want to leave <strong>{item.title}</strong>?</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild><button type="button" className="btn btn-secondary">Cancel</button></DialogClose>
                  <button type="button" className="btn btn-error" onClick={handleLeave}>Leave</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {isOwner && (
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
              <DialogTrigger asChild>
                <button type="button" className="flex items-center gap-2 w-full rounded-xl hover:bg-neutral px-3 py-2 text-sm cursor-pointer text-error">
                  <Trash2 size={14} /> Delete
                </button>
              </DialogTrigger>
              <DialogContent className="bg-secondary border-transparent rounded-3xl shadow-short">
                <DialogHeader>
                  <DialogTitle className="mb-3">Delete group chat?</DialogTitle>
                  <DialogDescription>Are you sure that you want to delete <strong>{item.title}</strong>?</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <DialogClose asChild><button type="button" className="btn btn-secondary">Cancel</button></DialogClose>
                  <button type="button" className="btn btn-error" onClick={handleDelete}>Delete</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default GroupChatHistoryList;
